import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Logo } from "../components/Logo";
import { API, formatApiErrorDetail } from "../lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      navigate("/admin");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors focus:border-cyan-400/60";

  return (
    <div className="grid-bg flex min-h-screen items-center justify-center bg-canvas px-5" data-testid="admin-login-page">
      <div className="w-full max-w-md rounded-2xl border border-cyan-500/20 bg-[#0D152D] p-8 shadow-[0_0_60px_rgba(0,102,255,0.15)]">
        <Link to="/" data-testid="admin-login-logo">
          <Logo />
        </Link>
        <h1 className="font-display mt-8 flex items-center gap-2 text-xl font-bold text-slate-50">
          <Lock size={18} className="text-cyan-400" />
          Owner login
        </h1>
        <p className="mt-1 text-sm text-slate-400">Access the enquiries dashboard.</p>

        <form onSubmit={submit} className="mt-7 space-y-5" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          {error && (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-6 py-3 text-sm font-semibold text-[#04101F] transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60"
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
